// Interface Admin - Dashboard Principal
import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  FiUsers, FiActivity, FiCreditCard, FiTrendingUp, FiServer,
  FiAlertCircle, FiCheckCircle, FiSettings, FiBarChart2,
  FiKey, FiFileText, FiDatabase, FiCpu, FiHardDrive
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

// Types
interface SystemHealth {
  cpu: number;
  ram: number;
  storage: number;
  uptime: number;
}

interface OrganizationStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  pendingUsers: number;
}

interface BillingInfo {
  plan: string;
  scoringsUsed: number;
  scoringsLimit: number;
  storageUsed: number;
  storageLimit: number;
  nextBilling: string;
  amount: number;
}

interface ActivityLog {
  id: string;
  time: string;
  type: 'scoring' | 'user' | 'commission' | 'system';
  description: string;
}

interface Alert {
  id: string;
  level: 'critical' | 'warning' | 'info';
  message: string;
  timestamp: Date;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const AdminBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: ${theme.colors.error}20;
  border: 2px solid ${theme.colors.error};
  color: ${theme.colors.error};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.bold};
  margin-top: ${theme.spacing.md};
  text-transform: uppercase;
`;

const TopMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};
`;

const MetricCard = styled.div<{ color?: string }>`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  border-left: 4px solid ${props => props.color || theme.colors.primary};
  transition: all ${theme.transitions.fast};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.md};
  }
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${theme.spacing.md};
`;

const MetricTitle = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  font-weight: ${theme.typography.weights.semibold};
`;

const MetricIcon = styled.div<{ bgColor?: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.bgColor || theme.colors.primary + '20'};
  color: ${props => props.bgColor?.replace('20', '') || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const MetricValue = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const MetricSubtext = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const MetricProgress = styled.div`
  margin-top: ${theme.spacing.md};
`;

const ProgressBar = styled.div`
  height: 8px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percentage: number; color?: string }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => props.color || theme.colors.primary};
  transition: width 0.5s ease;
`;

const QuickActions = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
`;

const ActionButton = styled.button`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}50;
  border: 2px solid transparent;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: white;
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.sm};
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.xl};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ActivitySection = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: start;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
  }
`;

const ActivityIcon = styled.div<{ type: string }>`
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;

  ${props => {
    switch (props.type) {
      case 'scoring':
        return `
          background: ${theme.colors.primary}20;
          color: ${theme.colors.primary};
        `;
      case 'user':
        return `
          background: ${theme.colors.info}20;
          color: ${theme.colors.info};
        `;
      case 'commission':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'system':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      default:
        return `
          background: ${theme.colors.text}20;
          color: ${theme.colors.text};
        `;
    }
  }}
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTime = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-bottom: 4px;
`;

const ActivityDescription = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const AlertsSection = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const AlertsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const AlertItem = styled.div<{ level: 'critical' | 'warning' | 'info' }>`
  padding: ${theme.spacing.md};
  border-left: 4px solid;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: start;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.sm};

  ${props => {
    switch (props.level) {
      case 'critical':
        return `
          background: ${theme.colors.error}10;
          border-color: ${theme.colors.error};
          color: ${theme.colors.error};
        `;
      case 'warning':
        return `
          background: ${theme.colors.warning}10;
          border-color: ${theme.colors.warning};
          color: ${theme.colors.warning};
        `;
      default:
        return `
          background: ${theme.colors.info}10;
          border-color: ${theme.colors.info};
          color: ${theme.colors.info};
        `;
    }
  }}
`;

const SystemHealthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`;

const HealthItem = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
`;

const HealthLabel = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-bottom: ${theme.spacing.xs};
`;

const HealthValue = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  const systemHealth: SystemHealth = {
    cpu: 23,
    ram: 41,
    storage: 67,
    uptime: 99.98
  };

  const orgStats: OrganizationStats = {
    totalUsers: 45,
    activeUsers: 38,
    inactiveUsers: 5,
    pendingUsers: 2
  };

  const billingInfo: BillingInfo = {
    plan: 'Enterprise',
    scoringsUsed: 1234,
    scoringsLimit: 2000,
    storageUsed: 234,
    storageLimit: 500,
    nextBilling: '2024-02-15',
    amount: 999
  };

  const activityLogs: ActivityLog[] = [
    {
      id: '1',
      time: '15:32',
      type: 'scoring',
      description: 'Nouveau scoring complété (#8934)'
    },
    {
      id: '2',
      time: '15:28',
      type: 'user',
      description: 'User marie.m connecté'
    },
    {
      id: '3',
      time: '15:25',
      type: 'commission',
      description: 'Commission terminée (5 attributions)'
    },
    {
      id: '4',
      time: '15:20',
      type: 'system',
      description: 'Backup automatique effectué'
    },
    {
      id: '5',
      time: '15:15',
      type: 'scoring',
      description: 'Validation manager (#8933)'
    }
  ];

  const alerts: Alert[] = [
    {
      id: '1',
      level: 'warning',
      message: 'Stockage à 67% - Envisager upgrade',
      timestamp: new Date(Date.now() - 3600000)
    },
    {
      id: '2',
      level: 'info',
      message: '2 utilisateurs en attente d\'activation',
      timestamp: new Date(Date.now() - 7200000)
    }
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'scoring': return <FiBarChart2 />;
      case 'user': return <FiUsers />;
      case 'commission': return <FiCheckCircle />;
      case 'system': return <FiServer />;
      default: return <FiActivity />;
    }
  };

  const getHealthColor = (percentage: number): string => {
    if (percentage >= 80) return theme.colors.error;
    if (percentage >= 60) return theme.colors.warning;
    return theme.colors.success;
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <Title>Administration Système</Title>
          <Subtitle>Vue 360° de votre organisation</Subtitle>
          <AdminBadge>
            <FiSettings /> Accès Administrateur
          </AdminBadge>
        </Header>

        <TopMetrics>
          <MetricCard color={theme.colors.primary}>
            <MetricHeader>
              <MetricTitle>Organisation</MetricTitle>
              <MetricIcon bgColor={theme.colors.primary + '20'}>
                <FiUsers />
              </MetricIcon>
            </MetricHeader>
            <MetricValue>{orgStats.totalUsers}</MetricValue>
            <MetricSubtext>Utilisateurs totaux</MetricSubtext>
            <MetricProgress>
              <div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '80', marginBottom: '4px' }}>
                Actifs: {orgStats.activeUsers} | Inactifs: {orgStats.inactiveUsers}
              </div>
              <ProgressBar>
                <ProgressFill
                  percentage={(orgStats.activeUsers / orgStats.totalUsers) * 100}
                  color={theme.colors.success}
                />
              </ProgressBar>
            </MetricProgress>
          </MetricCard>

          <MetricCard color={theme.colors.success}>
            <MetricHeader>
              <MetricTitle>Santé Système</MetricTitle>
              <MetricIcon bgColor={theme.colors.success + '20'}>
                <FiActivity />
              </MetricIcon>
            </MetricHeader>
            <MetricValue>{systemHealth.uptime}%</MetricValue>
            <MetricSubtext>Uptime</MetricSubtext>
            <SystemHealthGrid>
              <HealthItem>
                <HealthLabel>CPU</HealthLabel>
                <HealthValue style={{ color: getHealthColor(systemHealth.cpu) }}>
                  {systemHealth.cpu}%
                </HealthValue>
                <ProgressBar>
                  <ProgressFill percentage={systemHealth.cpu} color={getHealthColor(systemHealth.cpu)} />
                </ProgressBar>
              </HealthItem>
              <HealthItem>
                <HealthLabel>RAM</HealthLabel>
                <HealthValue style={{ color: getHealthColor(systemHealth.ram) }}>
                  {systemHealth.ram}%
                </HealthValue>
                <ProgressBar>
                  <ProgressFill percentage={systemHealth.ram} color={getHealthColor(systemHealth.ram)} />
                </ProgressBar>
              </HealthItem>
            </SystemHealthGrid>
          </MetricCard>

          <MetricCard color={theme.colors.warning}>
            <MetricHeader>
              <MetricTitle>Facturation</MetricTitle>
              <MetricIcon bgColor={theme.colors.warning + '20'}>
                <FiCreditCard />
              </MetricIcon>
            </MetricHeader>
            <MetricValue>{billingInfo.amount} FCFA</MetricValue>
            <MetricSubtext>Plan: {billingInfo.plan}</MetricSubtext>
            <MetricProgress>
              <div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '80', marginBottom: '4px' }}>
                Scorings: {billingInfo.scoringsUsed}/{billingInfo.scoringsLimit}
              </div>
              <ProgressBar>
                <ProgressFill
                  percentage={(billingInfo.scoringsUsed / billingInfo.scoringsLimit) * 100}
                  color={theme.colors.primary}
                />
              </ProgressBar>
              <div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '80', marginTop: '8px' }}>
                Stockage: {billingInfo.storageUsed}/{billingInfo.storageLimit} GB
              </div>
              <ProgressBar>
                <ProgressFill
                  percentage={(billingInfo.storageUsed / billingInfo.storageLimit) * 100}
                  color={getHealthColor((billingInfo.storageUsed / billingInfo.storageLimit) * 100)}
                />
              </ProgressBar>
            </MetricProgress>
          </MetricCard>
        </TopMetrics>

        <QuickActions>
          <SectionTitle>
            <FiSettings /> Actions Rapides
          </SectionTitle>
          <ActionsGrid>
            <ActionButton onClick={() => navigate('/admin/users')}>
              <FiUsers /> Gérer Utilisateurs
            </ActionButton>
            <ActionButton onClick={() => navigate('/admin/config')}>
              <FiSettings /> Configuration
            </ActionButton>
            <ActionButton onClick={() => navigate('/admin/billing')}>
              <FiCreditCard /> Facturation
            </ActionButton>
            <ActionButton onClick={() => navigate('/admin/integrations')}>
              <FiKey /> Intégrations API
            </ActionButton>
            <ActionButton onClick={() => navigate('/admin/analytics')}>
              <FiBarChart2 /> Rapports
            </ActionButton>
            <ActionButton onClick={() => navigate('/admin/compliance')}>
              <FiFileText /> Conformité
            </ActionButton>
          </ActionsGrid>
        </QuickActions>

        <TwoColumnLayout>
          <ActivitySection>
            <SectionTitle>
              <FiActivity /> Activité Récente
            </SectionTitle>
            <ActivityList>
              {activityLogs.map(log => (
                <ActivityItem key={log.id}>
                  <ActivityIcon type={log.type}>
                    {getActivityIcon(log.type)}
                  </ActivityIcon>
                  <ActivityContent>
                    <ActivityTime>{log.time}</ActivityTime>
                    <ActivityDescription>{log.description}</ActivityDescription>
                  </ActivityContent>
                </ActivityItem>
              ))}
            </ActivityList>
          </ActivitySection>

          <AlertsSection>
            <SectionTitle>
              <FiAlertCircle /> Alertes
            </SectionTitle>
            <AlertsList>
              {alerts.map(alert => (
                <AlertItem key={alert.id} level={alert.level}>
                  <FiAlertCircle />
                  <div>{alert.message}</div>
                </AlertItem>
              ))}
              {alerts.length === 0 && (
                <AlertItem level="info">
                  <FiCheckCircle />
                  <div>Aucune alerte. Système fonctionnel.</div>
                </AlertItem>
              )}
            </AlertsList>

            <div style={{ marginTop: theme.spacing.lg }}>
              <SectionTitle style={{ fontSize: theme.typography.sizes.base }}>
                <FiDatabase /> Stockage
              </SectionTitle>
              <HealthItem>
                <HealthLabel>Utilisation</HealthLabel>
                <HealthValue style={{ color: getHealthColor(systemHealth.storage) }}>
                  {systemHealth.storage}%
                </HealthValue>
                <ProgressBar>
                  <ProgressFill percentage={systemHealth.storage} color={getHealthColor(systemHealth.storage)} />
                </ProgressBar>
                <div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '60', marginTop: '8px' }}>
                  {billingInfo.storageUsed} GB / {billingInfo.storageLimit} GB
                </div>
              </HealthItem>
            </div>
          </AlertsSection>
        </TwoColumnLayout>
      </Container>
    </DashboardLayout>
  );
};
